import { useMutation } from "react-query";
import api from "api";
import { Message, MessagesResponse, MAX_PAGES, convertToCSV, downloadCSV } from "utils/messageExport";

interface ExportParams {
  mode: "patient" | "therapist";
  contextId: number;
  relatedId: number;
  from_date?: string;
  to_date?: string;
}

export const useExportMessages = () => {
  return useMutation(async ({ mode, contextId, relatedId, from_date, to_date }: ExportParams) => {
    const allMessages: Message[] = [];
    let cursor: string | undefined;
    let hasNext = true;
    let pageCount = 0;

    // Convert date strings (YYYY-MM-DD) to ISO 8601 format if provided
    const fromDateTime = from_date ? `${from_date}T00:00:00Z` : undefined;
    const toDateTime = to_date ? `${to_date}T23:59:59Z` : undefined;

    // Build query params based on mode
    const isPatientMode = mode === "patient";
    const baseQuery = isPatientMode
      ? { patient_id: contextId, therapist_id: relatedId }
      : { therapist_id: contextId, patient_id: relatedId };

    // Loop through ALL pages to get complete message history
    while (hasNext && pageCount < MAX_PAGES) {
      pageCount++;

      const response = await api("GET /messages", {
        query: {
          ...baseQuery,
          from_date: fromDateTime,
          to_date: toDateTime,
          "page[after]": cursor,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch messages: ${response.status}`);
      }

      const data: MessagesResponse = await response.json();

      allMessages.push(...(data.items || []));

      hasNext = data.metadata?.pagination?.has_next ?? false;
      cursor = data.metadata?.pagination?.cursor;
    }

    // Check if we hit the pagination limit
    if (hasNext && pageCount >= MAX_PAGES) {
      throw new Error(
        `Export exceeded maximum page limit (${MAX_PAGES} pages). Please use date filters to narrow the results.`
      );
    }

    // Convert to CSV and trigger download
    const csv = convertToCSV(allMessages);
    const filename = isPatientMode
      ? `patient-${contextId}-therapist-${relatedId}-messages-${new Date().toISOString().split("T")[0]}.csv`
      : `therapist-${contextId}-patient-${relatedId}-messages-${new Date().toISOString().split("T")[0]}.csv`;
    downloadCSV(csv, filename);
  });
};
